import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentFormProps {
  amount: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function PaymentForm({ amount, onSuccess, onError }: PaymentFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const setupStripe = async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Failed to load Stripe');

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ amount }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Payment failed');
        }

        const { clientSecret } = await response.json();

        const elements = stripe.elements({
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#4F46E5',
            },
          },
        });

        const paymentElement = elements.create('payment');
        paymentElement.mount('#payment-element');

        const form = document.getElementById('payment-form');
        if (!form) throw new Error('Payment form not found');

        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          setLoading(true);
          setError(null);

          try {
            const { error: stripeError } = await stripe.confirmPayment({
              elements,
              clientSecret,
              confirmParams: {
                return_url: `${window.location.origin}/payment-success`,
              },
            });

            if (stripeError) {
              throw stripeError;
            }

            onSuccess?.();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Payment failed');
            onError?.(err instanceof Error ? err : new Error('Payment failed'));
          } finally {
            setLoading(false);
          }
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to setup payment');
        onError?.(err instanceof Error ? err : new Error('Failed to setup payment'));
      }
    };

    setupStripe();
  }, [amount]);

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
      <form id="payment-form" className="space-y-6">
        <div id="payment-element" className="min-h-[15rem]" />
        
        {error && (
          <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Processing...' : `Pay $${(amount / 100).toFixed(2)}`}
        </button>
      </form>
    </div>
  );
}